import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  Item,
  CreateItemData,
  UpdateItemData,
  ItemsResponse,
  ItemResponse,
  ItemsListParams,
  ItemsListResult,
} from '../types/item';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getItems = async (params?: ItemsListParams): Promise<ItemsListResult> => {
  const queryParams = new URLSearchParams();

  if (params?.search) {
    queryParams.set('search', params.search);
  }
  if (params?.category_id != null) {
    queryParams.set('category_id', String(params.category_id));
  }
  if (params?.item_type) {
    queryParams.set('item_type', params.item_type);
  }
  if (params?.active != null) {
    queryParams.set('active', params.active ? '1' : '0');
  }
  if (params?.sort) {
    queryParams.set('sort', params.sort);
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEMS.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch items');
  }

  const result: ItemsResponse = await response.json();
  const meta = result.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: result.data?.length ?? 0,
    total: result.data?.length ?? 0,
  };

  return {
    items: result.data ?? [],
    meta,
  };
};

/** Fetches every page (for dropdowns and pickers that need the full catalog). */
export const getAllItems = async (): Promise<Item[]> => {
  const all: Item[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await getItems({ per_page: 100, page });
    all.push(...result.items);
    lastPage = result.meta.last_page;
    page += 1;
  } while (page <= lastPage);

  return all;
};

export const getItemById = async (id: number): Promise<Item> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEMS.GET_BY_ID}/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch item');
  }

  const result: ItemResponse = await response.json();
  return result.data;
};

export const createItem = async (itemData: CreateItemData): Promise<Item> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEMS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(itemData),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create item');
  }

  const result: ItemResponse = await response.json();
  return result.data;
};

export const updateItem = async (itemId: number, itemData: UpdateItemData): Promise<Item> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEMS.UPDATE}/${itemId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(itemData),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update item');
  }

  const result: ItemResponse = await response.json();
  return result.data;
};

export const deleteItem = async (itemId: number): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEMS.DELETE}/${itemId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete item');
  }
};
